import { createFileRoute } from '@tanstack/react-router';
import { useTranslation } from 'react-i18next';
import { communityTrpc } from '@org/clients/community';
import { graphTrpc } from '@org/clients/graph';
import { useAuth } from '@org/ui';
import { PostCard } from '@/components/post-card';
import { FloatingCreateButton } from '@/components/floating-create-button';
import { Button, Skeleton } from '@org/ui';
import { ConnectedUpcomingEvents, ConnectedTrendingPosts } from '@org/ui';
import { Plus, Flame, Clock } from 'lucide-react';
import { useState } from 'react';
import { Link } from '@tanstack/react-router';

export const Route = createFileRoute('/')({
  component: HomePage,
});

type SortBy = 'hot' | 'recent';

function HomePage() {
  const { t } = useTranslation('community');
  const { user } = useAuth();
  const [sortBy, setSortBy] = useState<SortBy>('recent');

  const { data: feedData, isLoading, fetchNextPage, hasNextPage, isFetchingNextPage } =
    communityTrpc.posts.getFeed.useInfiniteQuery(
      { limit: 20, sortBy },
      {
        getNextPageParam: (lastPage, allPages) => {
          if (lastPage.posts.length < 20) return undefined;
          return allPages.length * 20;
        },
        initialCursor: 0,
      }
    );

  const posts = feedData?.pages.flatMap((page) => page.posts) ?? [];

  const sortOptions: { value: SortBy; label: string; icon: React.ReactNode }[] = [
    { value: 'recent', label: t('feed.newest'), icon: <Clock className="h-4 w-4" /> },
    { value: 'hot', label: t('feed.hottest'), icon: <Flame className="h-4 w-4" /> },
  ];

  return (
    <div className="flex gap-6">
      {/* Main feed */}
      <div className="min-w-0 flex-1 space-y-6">
        {/* Header */}
        <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
          <h1 className="text-2xl font-bold">{t('feed.title')}</h1>
          <Link to="/posts/new">
            <Button className="gap-2">
              <Plus className="h-4 w-4" />
              {t('feed.newPost')}
            </Button>
          </Link>
        </div>

        {/* Sort Options */}
        <div className="flex gap-2" role="group" aria-label={t('feed.sortBy')}>
          {sortOptions.map((option) => (
            <Button
              key={option.value}
              variant={sortBy === option.value ? 'default' : 'outline'}
              size="sm"
              onClick={() => setSortBy(option.value)}
              aria-pressed={sortBy === option.value}
              className="gap-2"
            >
              {option.icon}
              {option.label}
            </Button>
          ))}
        </div>

        {/* Posts */}
        <div className="space-y-4">
          {isLoading ? (
            Array.from({ length: 3 }).map((_, i) => (
              <div key={i} className="rounded-2xl border border-border bg-card p-4">
                <div className="flex items-start gap-3">
                  <Skeleton className="h-10 w-10 rounded-full" />
                  <div className="flex-1 space-y-2">
                    <Skeleton className="h-4 w-32" />
                    <Skeleton className="h-4 w-full" />
                    <Skeleton className="h-4 w-3/4" />
                  </div>
                </div>
              </div>
            ))
          ) : posts.length === 0 ? (
            <div className="rounded-2xl border border-border bg-card p-8 text-center">
              <Plus className="mx-auto h-12 w-12 text-muted-foreground" />
              <p className="mt-4 text-muted-foreground">{t('feed.emptyFeed')}</p>
              <Link to="/posts/new" className="mt-4 inline-block">
                <Button size="sm" className="gap-2">
                  <Plus className="h-4 w-4" />
                  {t('feed.newPost')}
                </Button>
              </Link>
            </div>
          ) : (
            posts.map((post) => <PostCard key={post.postId} post={post} />)
          )}
        </div>

        {/* Load More */}
        {hasNextPage && (
          <div className="flex justify-center">
            <Button
              variant="outline"
              onClick={() => fetchNextPage()}
              disabled={isFetchingNextPage}
            >
              {isFetchingNextPage ? t('common.loading') : t('feed.loadMore')}
            </Button>
          </div>
        )}

        <FloatingCreateButton />
      </div>

      {/* Right rail — lg+ only */}
      <aside className="hidden w-72 shrink-0 lg:block" aria-label={t('feed.rightRail')}>
        <div className="sticky top-4 space-y-4">
          <ConnectedUpcomingEvents
            viewAllHref="/events"
            title={t('rightRail.upcomingEvents', { defaultValue: 'Upcoming events' })}
            emptyLabel={t('rightRail.noEvents', { defaultValue: 'No upcoming events' })}
            viewAllLabel={t('rightRail.viewAll', { defaultValue: 'View all' })}
            labels={{
              today: t('rightRail.today'),
              tomorrow: t('rightRail.tomorrow'),
              startsInMinutes: (count) => t('rightRail.startsInMinutes', { count }),
              startsAt: (time) => t('rightRail.startsAt', { time }),
              kind: {
                group: t('rightRail.kind.group'),
                personal: t('rightRail.kind.personal'),
                event: t('rightRail.kind.event'),
              },
            }}
          />
          <ConnectedTrendingPosts
            viewAllHref="/search"
            title={t('rightRail.trendingPosts', { defaultValue: 'Trending posts' })}
            emptyLabel={t('rightRail.noPosts', { defaultValue: 'No trending posts' })}
            viewAllLabel={t('rightRail.viewAll', { defaultValue: 'View all' })}
          />
        </div>
      </aside>
    </div>
  );
}
